import { Briefcase, IndianRupee, Send, Users } from "lucide-react";
import PartnerGate from "./Gate";
import { StatCard, StatusBadge, inr } from "@/components/bits";
import api from "@/lib/api";
import { useEffect, useState } from "react";

export default function Overview() {
  const [recent, setRecent] = useState([]);
  useEffect(() => {
    api.get("/partner/submissions").then(({ data }) => setRecent(data.submissions.slice(0, 5))).catch(() => {});
  }, []);

  return (
    <PartnerGate>
      {(overview) => (
        <div data-testid="partner-hub">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <div>
              <h2 className="font-heading font-bold text-xl text-[#0B1B3D]">Welcome, {overview.partner?.name}</h2>
              <p className="text-xs text-muted-foreground mt-1">
                Plan: <span className="font-semibold text-[#0B1B3D]" data-testid="overview-plan">{overview.subscription?.plan_name}</span>
                {" · "}valid till {overview.subscription?.expires_at?.slice(0, 10)}
                {overview.subscription?.scope === "global" ? " · Domestic + International" : " · Domestic only"}
              </p>
            </div>
            {overview.payout_tier?.current && (
              <span className="text-xs font-semibold text-[#0B1B3D] bg-[#F5A623]/10 border border-[#F5A623]/30 rounded-full px-3 py-1.5" data-testid="overview-tier">
                {overview.payout_tier.current.label} payout tier
              </span>
            )}
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard label="Open Assignments" value={overview.openings ?? 0} icon={Briefcase} testId="stat-openings" />
            <StatCard label="Submitted Candidates" value={overview.submissions ?? 0} icon={Users} testId="stat-submissions" />
            <StatCard label="Placements" value={overview.placements ?? 0} icon={Send} testId="stat-placements" />
            <StatCard label="Earnings (Paid)" value={inr(overview.earnings_paid)} icon={IndianRupee} accent testId="stat-earnings" />
          </div>

          <div className="grid lg:grid-cols-2 gap-6 mt-8">
            <div className="bg-white border border-border/60 rounded-xl p-6" data-testid="overview-pipeline">
              <h3 className="font-heading font-semibold text-[#0B1B3D] mb-4">Pipeline breakdown</h3>
              {Object.keys(overview.stage_counts || {}).length === 0 ? (
                <p className="text-sm text-muted-foreground">No candidates in the pipeline yet. Submit your first candidate from Openings.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {Object.entries(overview.stage_counts).map(([stage, count]) => (
                    <span key={stage} className="inline-flex items-center gap-2 text-xs bg-[#F8FAFC] border border-border/60 rounded-full px-3 py-1.5" data-testid={`stage-${stage}`}>
                      <StatusBadge status={stage} /> <span className="font-bold">{count}</span>
                    </span>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-2 gap-4 mt-5 pt-5 border-t border-border/60 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground">Earnings pending</p>
                  <p className="font-heading font-bold text-[#0B1B3D]" data-testid="overview-pending">{inr(overview.earnings_pending)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">CRM pipeline value</p>
                  <p className="font-heading font-bold text-[#0B1B3D]" data-testid="overview-pipeline-value">{inr(overview.pipeline_value || 0)}</p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-border/60 rounded-xl p-6" data-testid="overview-recent">
              <h3 className="font-heading font-semibold text-[#0B1B3D] mb-4">Recent submissions</h3>
              {recent.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nothing yet — your latest candidate submissions will appear here.</p>
              ) : (
                <div className="space-y-3">
                  {recent.map((s) => (
                    <div key={s.id} className="flex items-center justify-between gap-3 text-sm" data-testid={`recent-${s.id}`}>
                      <div className="min-w-0">
                        <p className="font-semibold truncate">{s.candidate_name}</p>
                        <p className="text-xs text-muted-foreground truncate">{s.job_title}</p>
                      </div>
                      <StatusBadge status={s.stage} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </PartnerGate>
  );
}
